import { TileEdgeType } from '@colonist/game-states';
import { MechanicValidator } from './MechanicValidator.ts';
import { MechanicValidatorKnightPlayer } from './MechanicValidatorKnightPlayer.ts';
import { MapStateDepthFirstSearch } from '../core/map/MapStateDepthFirstSearch.ts';
export class MechanicValidatorKnight extends MechanicValidator {
    getKnightPlayerValidators() {
        return this.knightPlayerValidators;
    }
    getKnightPlayerValidator(player) {
        const knightPlayerValidator = this.knightPlayerValidators.find((validator)=>validator.playerColor == player);
        if (knightPlayerValidator === undefined) throw new Error(`No knightPlayerValidator for player ${player}`);
        return knightPlayerValidator;
    }
    getAnyKnightAtLocation(cornerIndex) {
        for (const knightPlayerValidator of this.knightPlayerValidators){
            const knightAtLocation = knightPlayerValidator.getKnightAtLocation(cornerIndex);
            if (knightAtLocation === undefined) continue;
            return knightAtLocation;
        }
        return undefined;
    }
    getTotalKnightStrength() {
        const playerKnightStrengths = this.knightPlayerValidators.map((knightPlayerValidator)=>knightPlayerValidator.getPlayerKnightStrength());
        return playerKnightStrengths.reduce((x, acc)=>x + acc);
    }
    getPlayersWithHighestKnightStrength() {
        const playerKnightStrengths = this.knightPlayerValidators.map((knightPlayerValidator)=>knightPlayerValidator.getPlayerKnightStrength());
        const highestPlayerKnightStrength = Math.max(...playerKnightStrengths);
        return this.getPlayersWithKnightStrength(highestPlayerKnightStrength);
    }
    getPlayersWithLowestKnightStrength(excludingPlayers = []) {
        const includedKnightPlayerValidators = this.knightPlayerValidators.filter((knightPlayerValidator)=>!excludingPlayers.includes(knightPlayerValidator.playerColor));
        const playerKnightStrengths = includedKnightPlayerValidators.map((knightPlayerValidator)=>knightPlayerValidator.getPlayerKnightStrength());
        const lowestPlayerKnightStrength = Math.min(...playerKnightStrengths);
        return this.getPlayersWithKnightStrength(lowestPlayerKnightStrength, excludingPlayers);
    }
    whereCanPlayerRemoveKnight(player) {
        const knightPlayerState = this.getKnightPlayerValidator(player);
        const whereCanPlayerRemoveKnight = [];
        for (const knight of knightPlayerState.getKnightStates()){
            if (knight.locationCornerIndex == undefined) continue;
            whereCanPlayerRemoveKnight.push(knight.locationCornerIndex);
        }
        return whereCanPlayerRemoveKnight;
    }
    whereCanPlayerMoveKnight(knightState, player, includeKnightDisplacement) {
        const allStartingEdgesToInitialSearch = this.getStartingEdgesForKnight(knightState);
        const allLocationsPlayerCanMoveKnight = [];
        for (const startingEdge of allStartingEdgesToInitialSearch){
            const mapDepthFirstSearch = this.runMapDepthFirstSearch(player, startingEdge);
            if (mapDepthFirstSearch == undefined) return [];
            const potentialEnemyKnightLocations = mapDepthFirstSearch.interConnectedEnemyBuildings;
            const whatKnightsCanBeDisplacedByKnight = includeKnightDisplacement ? this.whatKnightsCanPlayerDisplacedWithKnight(potentialEnemyKnightLocations, knightState) : [];
            const emptyLocationsToMoveKnight = mapDepthFirstSearch.interConnectedEmptyNodes;
            allLocationsPlayerCanMoveKnight.push(...whatKnightsCanBeDisplacedByKnight);
            allLocationsPlayerCanMoveKnight.push(...emptyLocationsToMoveKnight);
        }
        return allLocationsPlayerCanMoveKnight;
    }
    runMapDepthFirstSearch(player, startingEdgeToInitialSearch) {
        const ignoreMyBuildings = true;
        const ignoreEnemyBuildings = false;
        const roadAndShipEdgeMap = this.mapValidator.generateEdgeMapForPlayerAndPieces(player, [
            TileEdgeType.Road,
            TileEdgeType.Ship
        ]);
        const mapDepthFirstSearch = new MapStateDepthFirstSearch(player, roadAndShipEdgeMap, startingEdgeToInitialSearch, this.mapValidator, ignoreEnemyBuildings, ignoreMyBuildings);
        return mapDepthFirstSearch;
    }
    getStartingEdgesForKnight(knightState) {
        if (knightState.locationCornerIndex == undefined) return [];
        const startingEdgesForKnight = [];
        const touchingEdges = this.mapValidator.tileState.tileCorners[knightState.locationCornerIndex].hexCorner.touchingEdges();
        for (const touchingEdge of touchingEdges){
            const tileEdge = this.mapValidator.tileState.getTileEdge(touchingEdge);
            if (tileEdge == undefined) continue;
            if (tileEdge.owner != knightState.owner) continue;
            if (tileEdge.type == undefined) continue;
            startingEdgesForKnight.push(tileEdge);
        }
        return startingEdgesForKnight;
    }
    whatKnightsCanPlayerDisplacedWithKnight(potentialEnemyKnightLocations, knightTakingAction) {
        const knightsThatCanBeDisplaced = [];
        for (const potentialKnightLocation of potentialEnemyKnightLocations){
            const knightAtLocation = this.getAnyKnightAtLocation(potentialKnightLocation);
            if (knightAtLocation == undefined) continue;
            if (knightAtLocation.level >= knightTakingAction.level) continue;
            knightsThatCanBeDisplaced.push(potentialKnightLocation);
        }
        return knightsThatCanBeDisplaced;
    }
    getPlayersWithKnightStrength(strength, excludingPlayers = []) {
        const playersWithKnightStrength = [];
        for (const knightPlayerValidator of this.knightPlayerValidators){
            if (knightPlayerValidator.getPlayerKnightStrength() !== strength) continue;
            if (excludingPlayers.includes(knightPlayerValidator.playerColor)) continue;
            playersWithKnightStrength.push(knightPlayerValidator.playerColor);
        }
        return playersWithKnightStrength;
    }
    constructor(playerValidators, mapValidator, knightState){
        super(playerValidators, mapValidator);
        this.knightPlayerValidators = [];
        for(const playerColorStr in knightState){
            const playerColor = Number(playerColorStr);
            const knightPlayerState = knightState[playerColor];
            if (knightPlayerState == undefined) continue;
            const playerValidator = this.playerValidators.get(playerColor);
            if (playerValidator == undefined) continue;
            const knightPlayerValidator = new MechanicValidatorKnightPlayer(playerValidator, this.mapValidator, knightPlayerState);
            this.knightPlayerValidators.push(knightPlayerValidator);
        }
    }
}
